import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { CheckCircle, XCircle, CreditCard, Award, ArrowRight } from 'lucide-react';

// כתובת ה-iframe של נדרים פלוס - לאימות מול הפרטים שלך
const NEDARIM_IFRAME_URL = 'https://www.matara.pro/nedarimplus/iframe/';

const PLANS = {
  basic: { name: 'בסיסי', price: 29, features: ['עד 100 אישורים בחודש', 'תבנית אחת', 'דשבורד בסיסי'] },
  pro: { name: 'מקצועי', price: 79, features: ['עד 500 אישורים בחודש', 'תבניות ללא הגבלה', 'דשבורד מלא', 'לוג הורדות'] },
  unlimited: { name: 'ללא הגבלה', price: 149, features: ['אישורים ללא הגבלה', 'תבניות ללא הגבלה', 'כל הפיצ׳רים', 'תמיכה בעדיפות'] }
};

export default function PaymentPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const isSuccess = location.pathname.includes('success');
  const isFailed = location.pathname.includes('failed');

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [config, setConfig] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const iframeRef = useRef(null);
  const selectedPlanRef = useRef(null);

  // טעינת קונפיג נדרים
  useEffect(() => {
    api.get('/payments/nedarim-config').then(res => setConfig(res.data)).catch(() => {});
  }, []);

  // האזנה להודעות מה-iframe של נדרים
  useEffect(() => {
    function handleMessage(e) {
      if (!e.data || !e.data.Name) return;
      switch (e.data.Name) {
        case 'Height':
          if (iframeRef.current) iframeRef.current.style.height = (parseInt(e.data.Value) + 15) + 'px';
          break;
        case 'TransactionResponse':
          handleResponse(e.data.Value);
          break;
        default:
          break;
      }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
    // eslint-disable-next-line
  }, []);

  const handleResponse = async (resp) => {
    setProcessing(false);
    const ok = resp && (resp.Status === 'OK' || resp.Status === 'success' || resp.Status === '000');
    if (ok) {
      try {
        await api.post('/payments/confirm', {
          plan: selectedPlanRef.current,
          transactionId: resp.TransactionId || resp.Transaction || ''
        });
        navigate('/payment/success');
      } catch {
        setError('התשלום בוצע אך הייתה שגיאה בעדכון החשבון. פנה לתמיכה.');
      }
    } else {
      setError((resp && (resp.Message || resp.Error)) || 'התשלום נכשל, נסה שוב');
    }
  };

  const selectPlan = (key) => {
    setSelectedPlan(key);
    selectedPlanRef.current = key;
    setError('');
  };

  const startPayment = () => {
    if (!iframeRef.current || !config || !selectedPlan) return;
    setProcessing(true);
    setError('');
    iframeRef.current.contentWindow.postMessage({
      Name: 'PostNedarim',
      Value: {
        Mosad: config.mosad,
        ApiValid: config.apiValid,
        PaymentType: 'Ragil',
        Currency: '1',
        Zeout: '',
        FirstName: '',
        LastName: '',
        Phone: '',
        Mail: '',
        Amount: String(PLANS[selectedPlan].price),
        Tashlumim: '1',
        Groupe: '',
        Comment: 'אישורי לימודים | חבילה: ' + PLANS[selectedPlan].name + ' | מקור: אתר',
        Param1: selectedPlan,
        Param2: 'ishur-saas-website',
        CallBack: '',
        CallBackMailError: ''
      }
    }, '*');
  };

  // מסך הצלחה
  if (isSuccess) return (
    <div style={{display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'var(--bg)'}}>
      <div className="card" style={{maxWidth:400, textAlign:'center'}}>
        <CheckCircle size={48} color="var(--success)" style={{margin:'0 auto 16px'}} />
        <h2 style={{marginBottom:8}}>התשלום בוצע בהצלחה!</h2>
        <p style={{color:'var(--text-muted)', marginBottom:20}}>החשבון שלך פעיל כעת</p>
        <button className="btn btn-primary btn-full" onClick={() => navigate('/dashboard')} style={{justifyContent:'center'}}>
          מעבר לממשק
        </button>
      </div>
    </div>
  );

  if (isFailed) return (
    <div style={{display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'var(--bg)'}}>
      <div className="card" style={{maxWidth:400, textAlign:'center'}}>
        <XCircle size={48} color="var(--danger)" style={{margin:'0 auto 16px'}} />
        <h2 style={{marginBottom:8}}>התשלום נכשל</h2>
        <button className="btn btn-primary btn-full" onClick={() => navigate('/payment')} style={{justifyContent:'center'}}>
          נסה שוב
        </button>
      </div>
    </div>
  );

  return (
    <div style={{minHeight:'100vh', background:'var(--bg)', padding:'40px 20px'}}>
      <div style={{maxWidth: selectedPlan ? 560 : 860, margin:'0 auto'}}>
        <div style={{textAlign:'center', marginBottom:32}}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'center', gap:10, marginBottom:8}}>
            <Award size={28} color="var(--primary)" />
            <h1 style={{fontSize:26, fontWeight:700}}>{selectedPlan ? 'תשלום' : 'בחר תוכנית'}</h1>
          </div>
          <p style={{color:'var(--text-muted)'}}>
            {selectedPlan ? `תוכנית ${PLANS[selectedPlan].name} · ₪${PLANS[selectedPlan].price}/חודש` : 'התחל ליהנות מכל התכונות'}
          </p>
        </div>

        {!selectedPlan ? (
          <div className="grid-3">
            {Object.entries(PLANS).map(([key, plan]) => (
              <div key={key} className="card" style={{textAlign:'center', position:'relative', border: key === 'pro' ? '2px solid var(--primary)' : undefined}}>
                {key === 'pro' && (
                  <div style={{position:'absolute', top:-12, left:'50%', transform:'translateX(-50%)', background:'var(--primary)', color:'white', padding:'3px 16px', borderRadius:20, fontSize:12, fontWeight:600}}>
                    מומלץ
                  </div>
                )}
                <h3 style={{fontSize:18, fontWeight:700, marginBottom:4}}>{plan.name}</h3>
                <div style={{fontSize:36, fontWeight:700, color:'var(--primary)', margin:'12px 0'}}>
                  ₪{plan.price}<span style={{fontSize:14, color:'var(--text-muted)', fontWeight:400}}>/חודש</span>
                </div>
                <ul style={{listStyle:'none', marginBottom:24, textAlign:'right'}}>
                  {plan.features.map((f, i) => (
                    <li key={i} style={{padding:'6px 0', fontSize:13, color:'var(--text-muted)', display:'flex', alignItems:'center', gap:8}}>
                      <CheckCircle size={14} color="var(--success)" />{f}
                    </li>
                  ))}
                </ul>
                <button className={`btn ${key === 'pro' ? 'btn-primary' : 'btn-secondary'} btn-full`} onClick={() => selectPlan(key)} style={{justifyContent:'center'}}>
                  <CreditCard size={16} /> בחר תוכנית
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="card">
            <button className="btn btn-secondary" onClick={() => setSelectedPlan(null)} style={{marginBottom:16}}>
              <ArrowRight size={16} /> חזרה לתוכניות
            </button>

            {/* iframe של נדרים */}
            <iframe
              ref={iframeRef}
              src={NEDARIM_IFRAME_URL}
              title="תשלום נדרים פלוס"
              style={{width:'100%', minHeight:300, border:'none'}}
            />

            {error && (
              <div style={{background:'#fee2e2', color:'var(--danger)', padding:'10px 14px', borderRadius:8, fontSize:13, marginTop:12, textAlign:'center'}}>
                {error}
              </div>
            )}

            <button className="btn btn-primary btn-full" onClick={startPayment} disabled={processing || !config} style={{justifyContent:'center', marginTop:16}}>
              <CreditCard size={16} /> {processing ? 'מעבד תשלום...' : `שלם ₪${PLANS[selectedPlan].price}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
